import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatDateShort, toCents } from "@/lib/format";
import { PARTNER_TYPES, COMMISSION_TYPES } from "@/lib/partnerConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FieldSelect } from "@/components/ui/field-select";
import { Loader2, AlertTriangle } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const label = "mb-1 block text-sm font-medium text-slate-700";

// PERCENTAGE => value is a percent. FIXED_AMOUNT => backend stores minor units (cents).
export function PartnerDialog({ open, onOpenChange, partner }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const isEdit = !!partner;
  const { data: locData } = useQuery({
    queryKey: ["locations", orgId],
    queryFn: async () => (await api.get("/api/locations", { params: { organization_id: orgId } })).data,
    enabled: open,
  });
  const locations = locData?.locations || [];

  const [f, setF] = useState({});
  useEffect(() => {
    if (!open) return;
    if (partner) {
      setF({
        name: partner.name, partner_type: partner.partner_type,
        contact_name: partner.contact_name || "", phone: partner.phone || "",
        email: partner.email || "", notes: partner.notes || "",
        location_ids: partner.location_ids || [],
        commission_type: partner.default_commission_type || "NONE",
        commission_value: partner.default_commission_value == null ? ""
          : partner.default_commission_type === "FIXED_AMOUNT"
            ? String(partner.default_commission_value / 100)
            : String(partner.default_commission_value),
      });
    } else {
      setF({ name: "", partner_type: "HOTEL", contact_name: "", phone: "", email: "",
             notes: "", location_ids: [], commission_type: "NONE", commission_value: "" });
    }
  }, [open, partner]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleLoc = (id) => setF((s) => ({ ...s, location_ids: (s.location_ids || []).includes(id)
    ? s.location_ids.filter((x) => x !== id) : [...(s.location_ids || []), id] }));

  const mut = useMutation({
    mutationFn: async () => {
      const ct = f.commission_type === "NONE" ? null : f.commission_type;
      let cv = null;
      if (ct && f.commission_value !== "") {
        cv = ct === "FIXED_AMOUNT" ? toCents(f.commission_value) : parseFloat(f.commission_value);
      }
      const body = {
        organization_id: orgId, name: f.name, partner_type: f.partner_type,
        contact_name: f.contact_name || null, phone: f.phone || null, email: f.email || null,
        notes: f.notes || null, location_ids: f.location_ids || [],
        default_commission_type: ct, default_commission_value: cv,
      };
      if (isEdit) return (await api.patch(`/api/partners/${partner.id}`, body, { params: { organization_id: orgId } })).data;
      return (await api.post("/api/partners", body)).data;
    },
    onSuccess: (d) => { toast.success(isEdit ? "Partenaire modifié" : `Partenaire ${d.partner_reference} créé`); qc.invalidateQueries(); onOpenChange(false); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="partner-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle>
          <DialogDescription className="sr-only">Coordonnées et règle de commission par défaut.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className={label}>Nom</label><input value={f.name || ""} onChange={set("name")} data-testid="partner-name-input" className={input} placeholder="Ex: Riad Al Madina" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Type</label><FieldSelect testId="partner-type-select" className="w-full" value={f.partner_type} onChange={(v) => setF((s) => ({ ...s, partner_type: v }))} options={Object.entries(PARTNER_TYPES).map(([k, v]) => ({ value: k, label: v }))} /></div>
            <div><label className={label}>Contact</label><input value={f.contact_name || ""} onChange={set("contact_name")} data-testid="partner-contact-input" className={input} placeholder="Personne à contacter" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Téléphone</label><input value={f.phone || ""} onChange={set("phone")} data-testid="partner-phone-input" className={input} /></div>
            <div><label className={label}>E-mail</label><input value={f.email || ""} onChange={set("email")} data-testid="partner-email-input" className={input} /></div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Commission par défaut</p>
            <div className="grid grid-cols-2 gap-3">
              <FieldSelect testId="partner-commission-type" className="w-full" value={f.commission_type} onChange={(v) => setF((s) => ({ ...s, commission_type: v }))}
                options={[{ value: "NONE", label: "Aucune" }, ...Object.entries(COMMISSION_TYPES).map(([k, v]) => ({ value: k, label: v }))]} />
              {f.commission_type !== "NONE" && (
                <input type="number" min="0" step="0.01" value={f.commission_value || ""} onChange={set("commission_value")} data-testid="partner-commission-value"
                  className={input} placeholder={f.commission_type === "PERCENTAGE" ? "%" : "Montant (MAD)"} />
              )}
            </div>
            <p className="mt-1.5 text-xs text-slate-400">Pré-remplie à la réservation. Modifiable au cas par cas ; l'historique reste figé.</p>
          </div>

          {locations.length > 1 && (
            <div><label className={label}>Points de vente (optionnel)</label>
              <div className="flex flex-wrap gap-1.5">
                {locations.map((l) => (
                  <button key={l.id} type="button" onClick={() => toggleLoc(l.id)} data-testid={`partner-loc-${l.id}`}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${(f.location_ids || []).includes(l.id) ? "border-primary bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{l.name}</button>
                ))}
              </div>
            </div>
          )}

          <div><label className={label}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} data-testid="partner-notes-input" className={input} /></div>
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !f.name?.trim()} data-testid="save-partner-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Enregistrer" : "Créer le partenaire"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeactivateDialog({ open, onOpenChange, partner }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [warn, setWarn] = useState(null);
  useEffect(() => { if (!open) setWarn(null); }, [open]);
  const isActive = partner?.status === "ACTIVE";

  const mut = useMutation({
    mutationFn: async (confirm) => {
      const url = isActive ? `/api/partners/${partner.id}/deactivate` : `/api/partners/${partner.id}/activate`;
      return (await api.post(url, isActive ? { confirm } : {}, { params: { organization_id: orgId } })).data;
    },
    onSuccess: () => { toast.success(isActive ? "Partenaire désactivé" : "Partenaire réactivé"); qc.invalidateQueries(); onOpenChange(false); setWarn(null); },
    onError: (e) => { const d = e.response?.data?.detail; if (d && d.type === "future_bookings") setWarn(d); else toast.error(formatApiError(d) || e.message); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="partner-deactivate-dialog">
        <DialogHeader><DialogTitle>{isActive ? "Désactiver le partenaire" : "Réactiver le partenaire"}</DialogTitle>
          <DialogDescription className="sr-only">Changer le statut du partenaire.</DialogDescription></DialogHeader>
        {!warn ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {isActive
                ? "Un partenaire inactif n'est plus sélectionnable sur de nouvelles réservations. L'historique et les commissions existantes restent inchangés."
                : "Le partenaire redeviendra sélectionnable sur les nouvelles réservations."}
            </p>
            <button onClick={() => mut.mutate(false)} disabled={mut.isPending} data-testid="confirm-partner-status-button"
              className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${isActive ? "bg-rose-600 hover:bg-rose-700" : "bg-primary hover:bg-blue-700"}`}>
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {isActive ? "Désactiver" : "Réactiver"}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="partner-deactivate-warning">
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{warn.message}<ul className="mt-2 space-y-1">{warn.bookings.map((b) => <li key={b.id} className="font-mono text-xs">{b.booking_reference} · {formatDateShort(b.booking_date)} {b.start_time}</li>)}</ul></div>
            </div>
            <button onClick={() => mut.mutate(true)} disabled={mut.isPending} data-testid="confirm-partner-deactivate-anyway"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Désactiver quand même
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
